import { useMemo } from 'react'
import { Award } from 'lucide-react'
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBadgesDistribution } from '@/services/admin/metrics.service'
import { CHART_COLORS } from './types'
import type { DashboardFilters } from './types'

interface Props {
  filters: DashboardFilters
}

const TIER_COLORS: Record<string, string> = {
  bronze: '#C97B2F',
  prata: '#C2C2C2',
  ouro: '#E5B647',
  platina: '#5A8FF5',
}

const TIER_LABELS: Record<string, string> = {
  bronze: 'Bronze',
  prata: 'Prata',
  ouro: 'Ouro',
  platina: 'Platina',
}

/**
 * Distribuição real de grants por badge. Usa endpoint dedicado
 * `/metrics/badges-distribution` que agrupa user_badges by badgeId
 * (o `/admin/badges` não popula earnedCount, por isso antes vinha 0).
 */
export function BadgesDistributionChart({ filters }: Props) {
  const { data, isLoading } = useBadgesDistribution(filters.companyIds)

  const { chartData, totalGrants } = useMemo(() => {
    if (!data) return { chartData: [], totalGrants: 0 }
    const sorted = [...data]
      .sort((a, b) => b.grants - a.grants)
      .map((b) => ({
        name: b.badgeName,
        value: b.grants,
        tier: b.tier,
        color: TIER_COLORS[b.tier] ?? CHART_COLORS.primary,
      }))
      .slice(0, 10)
    const total = data.reduce((s, x) => s + x.grants, 0)
    return { chartData: sorted, totalGrants: total }
  }, [data])

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50 h-full">
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="glass-card border-border/50 h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Award className="w-4 h-4 text-primary" />
          Distribuição de badges
        </CardTitle>
        <CardDescription>
          Top {chartData.length} de {data?.length ?? 0} badges · {totalGrants} grants
        </CardDescription>
      </CardHeader>
      <CardContent>
        {totalGrants === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nenhuma badge concedida ainda.
          </p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 0, right: 24, left: 0, bottom: 0 }}
              >
                <XAxis
                  type="number"
                  stroke="#71717A"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  dataKey="name"
                  type="category"
                  stroke="#71717A"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={140}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                  contentStyle={{
                    backgroundColor: 'rgba(15, 15, 18, 0.96)',
                    border: '1px solid rgba(242, 101, 34, 0.4)',
                    borderRadius: 8,
                    fontSize: 12,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                    padding: '8px 12px',
                  }}
                  labelStyle={{ color: '#ffffff', fontWeight: 700, marginBottom: 2 }}
                  itemStyle={{ color: '#f5f5f5', fontWeight: 500 }}
                  formatter={(value: number) => [`${value} grants`, '']}
                />
                <Bar
                  dataKey="value"
                  radius={[0, 6, 6, 0]}
                  label={{ position: 'right', fontSize: 11, fill: '#a1a1aa' }}
                >
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Legenda dos tiers — cor → significado */}
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-border/40 text-[10px] text-muted-foreground justify-center">
              {Object.entries(TIER_COLORS).map(([tier, color]) => (
                <span key={tier} className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
                  {TIER_LABELS[tier]}
                </span>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
